import { useEffect, useState } from 'react';
import { getPaymentsById } from '../../../../common/services/payments.api';
import { getPartnersById, getPartnersByWalletId } from '../../../../common/services/partners.api';
import { getVehicleById } from '../../../../common/services/vehicles.api';
import { PaymentInfo } from '../../../../types/payment.type';
import { IPartner } from '../../../../types/partners.type';
import { IVehicle } from '../../../../types/vehicle.type';
import { IBillingManagement } from '../../../../types/billing-management.type';

interface UseManagementBillingReturn {
  paymentDetails: PaymentInfo | null;
  partner: IPartner | null;
  partnerWallet: IPartner | null;
  vehicle: IVehicle | null;
  isLoading: boolean;
  error: string | null;
  paymentSplitWalletId: string;
  subscriptionId: string;
  percentualValue: number;
}

export const useManagementBilling = (payment: IBillingManagement | undefined): UseManagementBillingReturn => {
  const [paymentDetails, setPaymentDetails] = useState<PaymentInfo | null>(null);
  const [partner, setPartner] = useState<IPartner | null>(null);
  const [partnerWallet, setPartnerWallet] = useState<IPartner | null>(null);
  const [vehicle, setVehicle] = useState<IVehicle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Validação dentro do useEffect
    if (!payment || !payment.paymentId || !payment.partnerId || !payment.vehicleId || !payment.splitWalletId) {
      console.error("Payment details are incomplete:", payment);
      return;
    }

    async function fetchPaymentDetails(paymentId: string, partnerId: string, vehicleId: string, splitWalletId: string) {
      setIsLoading(true);
      setError(null);

      try {
        const [paymentDetails, partnerDetails, partnerWalletDetails, vehicleDetails] = await Promise.all([
          getPaymentsById(paymentId),
          getPartnersById(partnerId),
          getPartnersByWalletId(splitWalletId),
          getVehicleById(vehicleId)
        ]);

        setPaymentDetails(paymentDetails.data);
        setPartner(partnerDetails.data);
        setPartnerWallet(partnerWalletDetails.data);
        setVehicle(vehicleDetails.data);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Erro ao buscar detalhes';
        console.error("Erro ao buscar detalhes:", error);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }

    fetchPaymentDetails(payment.paymentId, payment.partnerId, payment.vehicleId, payment.splitWalletId);
  }, [payment]);

  // Lógica para calcular paymentSplitWalletId
  const history = paymentDetails?.history?.[paymentDetails.history.length - 1].data ?? null;
  const paymentSplitWalletId = Array.isArray(history?.split) && history.split.length > 0
    ? history.split[history.split.length - 1]?.walletId ?? ""
    : "";
  const subscriptionId = history?.subscription ?? "";
  const percentualValue = Array.isArray(history?.split) && history.split.length > 0
    ? history.split[history.split.length - 1]?.percentualValue ?? 0
    : 0;

  return {
    paymentDetails,
    partner,
    partnerWallet,
    vehicle,
    isLoading,
    error,
    paymentSplitWalletId,
    subscriptionId,
    percentualValue
  };
};